import React, { useEffect, useId, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import GuestProfileDialog from "./GuestProfileDialog";
import { BookerCompanyContact, BookerTypeTabs, companyBookerContactRequired } from "./BookerCompanyForm";

import { useGuestProfileLanguage } from "./guestProfileI18n";

const personName = (person) => [person.first_name, person.middle_name, person.last_name, person.second_last_name].filter(Boolean).join(" ");

export default function BookerSelectorPopup({ show, onClose, businessUnitId, onBookerSelected }) {
  const { t } = useGuestProfileLanguage();
  const [customerType, setCustomerType] = useState("person");
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [contactName, setContactName] = useState("");
  const [contactRequired, setContactRequired] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const busyRef = useRef(false);
  const searchId = useId();

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setCustomerType("person");
    setSearch("");
    setSelected(null);
    setContactName("");
    setFeedback("");
    setContactRequired(true);
    if (businessUnitId) {
      axios.get("/api/hotel-settings", { params: { business_unit_id: businessUnitId } })
        .then(({ data }) => { if (!cancelled) setContactRequired(companyBookerContactRequired(data)); })
        .catch(() => { /* The server also enforces the default required contact policy. */ });
    }
    return () => { cancelled = true; };
  }, [show, businessUnitId]);

  useEffect(() => {
    if (!show || customerType !== "person") return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setCustomers([]);
    axios.get("/api/customers", { params: { business_unit_id: businessUnitId } })
      .then(({ data }) => { if (!cancelled) setCustomers(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setLoadError("The guest profiles could not be loaded."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, customerType, businessUnitId, reload]);

  useEffect(() => {
    if (!show || customerType !== "company") return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    const timer = setTimeout(() => {
      axios.get("/api/booker-companies", { params: { business_unit_id: businessUnitId, search: search.trim(), limit: 100 } })
        .then(({ data }) => { if (!cancelled) setCompanies(Array.isArray(data?.companies) ? data.companies : []); })
        .catch(() => { if (!cancelled) { setCompanies([]); setLoadError("The company profiles could not be loaded."); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, search.trim() ? 200 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [show, customerType, businessUnitId, search, reload]);

  if (!show) return null;
  const isCompany = customerType === "company";
  const query = search.trim().toLowerCase();
  const results = isCompany ? companies : customers.filter((customer) => [
    personName(customer), customer.email, customer.phone, customer.country, customer.city, customer.document_number,
  ].filter(Boolean).join(" ").toLowerCase().includes(query));

  const selectBooker = async (event) => {
    event.preventDefault();
    if (busyRef.current) return;
    if (!selected) { setFeedback(isCompany ? "Select a company first." : "Select a person first."); return; }
    if (isCompany && contactRequired && !contactName.trim()) {
      setFeedback("Enter the full name of the person who contacted you to book for this company.");
      return;
    }
    busyRef.current = true;
    setSaving(true);
    setFeedback("");
    try {
      if (typeof onBookerSelected !== "function") throw new Error("The reservation booker selection is unavailable.");
      if (isCompany) {
        await onBookerSelected({ type: "company", company: selected, contactName: contactName.trim() || null });
      } else {
        const { data: customer } = await axios.get(`/api/customers/${selected.id}`, { params: { business_unit_id: businessUnitId } });
        await onBookerSelected({ type: "person", customer });
      }
      onClose?.();
    } catch (error) {
      setFeedback(error?.response?.data?.error || error?.message || t("The booker could not be assigned. Please try again."));
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  return (
    <GuestProfileDialog title={t("Select Booker")} subtitle="Choose a person or company to book this reservation group."
      contextLabel={isCompany ? t("Company booker") : t("Person booker")}
      contextHint="Search the customer catalog, then select a profile to assign."
      requiredFields={isCompany && contactRequired ? ["booker_contact_name"] : []}
      createLabel="Assign booker" footHint="The selected profile will be assigned to this reservation group."
      onClose={() => { if (!busyRef.current) onClose?.(); }} onSubmit={selectBooker}
      saving={saving} feedback={feedback} submitDisabled={!selected || loading}
    >
      <BookerTypeTabs value={customerType} onChange={(type) => {
        setCustomerType(type); setSelected(null); setSearch(""); setFeedback(""); setLoadError("");
      }} />
      <div className="booker-selector">
        <label className="booker-company-field" htmlFor={searchId}>
          <span>{isCompany ? t("Search companies") : t("Search people")}</span>
          <input id={searchId} type="search" value={search} maxLength={255} onChange={(event) => setSearch(event.target.value)}
            placeholder={isCompany ? t("Name, tax ID, email or phone") : t("Name, email, phone or document")} />
        </label>
        {isCompany && <BookerCompanyContact value={contactName} required={contactRequired}
          onChange={(event) => { setContactName(event.target.value); setFeedback(""); }} />}
        {selected && <p className="booker-selector-selected" role="status">{t("Selected:")} <strong>{isCompany ? selected.name : personName(selected)}</strong></p>}
        {loadError ? <div className="booker-profile-feedback" role="alert">{t(loadError)} <button type="button"
          className="booker-profile-btn booker-profile-btn--secondary" onClick={() => setReload((value) => value + 1)}>{t("Retry")}</button></div> : null}
        <div className="booker-selector-results" aria-busy={loading}>
          {loading ? <p className="booker-selector-empty" role="status">{t("Loading profiles...")}</p>
            : !results.length ? <p className="booker-selector-empty">{isCompany ? t("No companies found. Use Add booker to create a new profile.") : t("No people found. Use Add booker to create a new profile.")}</p>
              : results.map((profile) => (
                <button type="button" key={profile.id} className={`booker-selector-result${selected?.id === profile.id ? " is-selected" : ""}`}
                  aria-pressed={selected?.id === profile.id} onClick={() => { setSelected(profile); setFeedback(""); }}>
                  <span><strong>{isCompany ? profile.name : personName(profile) || t("Unnamed guest")}</strong>
                    <small>{[isCompany ? profile.tax_id : profile.document_number, profile.email, profile.phone].filter(Boolean).join(" · ") || t("No contact details")}</small></span>
                  <span className="booker-selector-result__action">{selected?.id === profile.id ? t("Selected") : t("Select")}</span>
                </button>
              ))}
        </div>
        {isCompany && companies.length === 100 ? <p className="booker-selector-empty">{t("Showing the first 100 matches. Refine your search to find another company.")}</p> : null}
      </div>
    </GuestProfileDialog>
  );
}
